import React from "react";
import {
  Button as ButtonBase,
  Grid as GridBase,
  Box,
  Typography,
} from "@mui/material";

import RpMUIModalBase from "@/components/modals/RpMUIModal";
import { CREATIVE_UPDATE_STATUS } from "../utils/extendConstants";

type CreativeUpdateStatus =
  (typeof CREATIVE_UPDATE_STATUS)[keyof typeof CREATIVE_UPDATE_STATUS];

type ConfirmMessageProps = {
  open?: boolean;
  creativeUpdateStatus?: CreativeUpdateStatus;
  onCancel?: () => void;
  onKeepCreative?: () => void;
  onUpdateCreative?: () => void;
};

const Grid = GridBase as React.ComponentType<Record<string, any>>;
const RpMUIModal = RpMUIModalBase as React.ComponentType<Record<string, any>>;
const Button = ButtonBase as React.ComponentType<Record<string, any>>;
const Img = "img" as unknown as React.ComponentType<Record<string, any>>;

function ConfirmMessage({
  open,
  creativeUpdateStatus,
  onCancel,
  onKeepCreative,
  onUpdateCreative,
}: ConfirmMessageProps) {
  const getMessage = () => {
    switch (creativeUpdateStatus) {
      case CREATIVE_UPDATE_STATUS.ALL_CAN_UPDATE:
        return (
          <Typography
            variant="body1"
            className="text-center"
            sx={{ lineHeight: 1.9 }}
          >
            You're almost done! Most often, users extend campaigns and keep
            running the same creative. However, you can select a new creative
            for each location for the extended time period if you wish. How do
            you want to proceed?
          </Typography>
        );
      case CREATIVE_UPDATE_STATUS.ALL_NOT_CAN_UPDATE:
        return (
          <Typography
            variant="body1"
            className="text-center"
            sx={{ lineHeight: 1.9 }}
          >
            You're almost done! The extension you selected is shorter than the
            minimum duration required for creative updates. Your campaign will
            continue with the current creative. If you'd like to update
            creatives, please choose a longer extension
            <br />
            (marked on the calendar as eligible for creative updates).
          </Typography>
        );
      case CREATIVE_UPDATE_STATUS.MIX_CAN_UPDATE:
      case CREATIVE_UPDATE_STATUS.MIX_NOT_CAN_UPDATE:
        return (
          <Box sx={{ textAlign: "left", lineHeight: 1.9 }}>
            <Typography variant="body1" className="mb-4">
              Some of your selected locations are eligible for creative updates,
              while others are not
            </Typography>
            <Box sx={{ mb: 2, "& li": { mb: 1 } }}>
              <Typography variant="body1" component="span">
                <strong>Eligible locations: </strong> You may update creative.
                <br />
                <strong>Ineligible locations: </strong> These will continue
                running with the current creative.
              </Typography>
            </Box>
            <Typography variant="body1" className="text-center mt-3">
              If you'd like all locations to update creative, please choose a
              longer extension (marked on the calendar as eligible for creative
              updates).
            </Typography>
          </Box>
        );
    }
  };

  const renderButton = () => {
    const KeepCreativeButton = (variant: "contained" | "outlined") => (
      <Grid size={{ xs: 12, md: "auto" }}>
        <Button
          onClick={onKeepCreative}
          variant={variant}
          color="primary"
          sx={{ width: { xs: "100%", md: "auto" } }}
          disableElevation
          className="px-4"
        >
          Keep Creative
        </Button>
      </Grid>
    );
    const UpdateCreativeButton = (color: "primary" | string = "primary") => (
      <Grid size={{ xs: 12, md: "auto" }}>
        <Button
          onClick={onUpdateCreative}
          variant="contained"
          color={color}
          sx={{ width: { xs: "100%", md: "auto" } }}
          disableElevation
          className="px-4"
        >
          Update Creative
        </Button>
      </Grid>
    );
    switch (creativeUpdateStatus) {
      case CREATIVE_UPDATE_STATUS.ALL_CAN_UPDATE:
        return (
          <>
            {KeepCreativeButton("outlined")}
            {UpdateCreativeButton("primary")}
          </>
        );
      case CREATIVE_UPDATE_STATUS.ALL_NOT_CAN_UPDATE:
        return KeepCreativeButton("contained");
      case CREATIVE_UPDATE_STATUS.MIX_CAN_UPDATE:
      case CREATIVE_UPDATE_STATUS.MIX_NOT_CAN_UPDATE:
        return (
          <>
            {KeepCreativeButton("outlined")}
            {UpdateCreativeButton("primary")}
          </>
        );
    }
  };

  return (
    <RpMUIModal open={open} onClose={onCancel} persists size="large">
      <Box className="p-2">
        <Box className="mb-4 text-center">
          <Img
            src="/img/icons/info.svg"
            className="mx-auto"
            sx={{ width: 100, height: 100 }}
            square
          />
        </Box>
        <Box sx={{ px: { xs: 2, sm: 4 }, mb: 4 }}>{getMessage()}</Box>

        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
          color="primary"
          sx={{ mt: 4 }}
        >
          <Grid size={{ xs: 12, md: "auto" }}>
            <Button
              onClick={onCancel}
              variant="outlined"
              color="default"
              className="px-5"
              disableElevation
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              Cancel
            </Button>
          </Grid>
          {renderButton()}
        </Grid>
      </Box>
    </RpMUIModal>
  );
}

export default ConfirmMessage;
